// =============================================================================
// positioning-client.ts — Client API FOURNISSEUR de l'indicateur de positionnement F-10
// (Cycle 2, DBU §3.6 / ADR D10), DOUBLABLE. RÉVISION UNAIRE (G0-S17-16).
// =============================================================================
// Miroir HTTP de l'endpoint FOURNISSEUR exposé par `referencement-api` (S17 / D1). RÉVISION UNAIRE
// (G0-S17-16) : l'indicateur F-10 est désormais un DRAPEAU SEUL par couple profil×niveau —
// `{ masked, orange }` — SANS bornes P25/P75. Le front ne DÉRIVE plus rien : l'autorité (API) a
// déjà comparé le TJM saisi aux bornes du marché et renvoie UNIQUEMENT le verdict orange/pas-orange
// (ou masqué). Le front ne connaît plus les bornes ; il ne peut donc rien reconstituer.
//
// AUTORITÉ = API (G0-S17, DBU §3.6) :
//   • Le CALCUL des bornes P25/P75 ET la COMPARAISON (tjm ≥ P75 ?) sont l'autorité de l'API ;
//   • Le MASQUAGE d'anonymat est l'autorité de l'API : si l'effectif du couple profil×niveau est
//     SOUS le seuil (effectif < max(K, 2) ; K = k_anonymity_threshold admin-modifiable),
//     l'API renvoie `masked: true` SANS aucun verdict exploitable (jamais P25/P75 ni effectif).
//   • `submission_count` (effectif) n'est JAMAIS transmis ni affiché côté fournisseur (G0-S17-3) :
//     le DTO ne porte AUCUN champ d'effectif ni AUCUNE borne. Le front ne peut RIEN reconstituer.
//
// CE QUE LE FRONT FAIT (et SEULEMENT cela, DBU §3.6 / G0-S17-16) :
//   • Il LIT le drapeau `orange` (TJM ≥ P75) par couple, quand l'indicateur n'est pas masqué.
//   • Il affiche un LIBELLÉ + ICÔNE uniquement quand `orange === true` (« Au-dessus du marché ») ;
//     sinon (pas-orange) il n'affiche RIEN ; masqué ⇒ message neutre. PLUS aucune dérivation
//     client-side, PLUS aucun tooltip de bornes.
//
// DTO = miroirs HTTP ASCII (PAS @jeko/db) : l'enum de séniorité est répliqué en union de
// littéraux (parité Prisma RateSeniorityLevel) — la SPA ne couple PAS au paquet DB. Aucune donnée
// métier en localStorage (RULE_REF_006). Horodatages = ISO 8601 (string), jamais d'objet Date ORM.

// --- Enum miroir HTTP (parité Prisma RateSeniorityLevel, ASCII — accents = i18n front) ---

/** Niveau de séniorité d'un couple (parité Prisma RateSeniorityLevel, schéma rate_collection). */
export type RateSeniorityLevelDto = 'Debutant' | 'Maitrisant' | 'Expert' | 'Referent';

// =============================================================================
// Indicateur de positionnement par couple profil×niveau — DRAPEAU SEUL (G0-S17-16). UNAIRE.
// =============================================================================

/**
 * Indicateur de positionnement UNAIRE d'un couple (profileCode, seniorityLevel). DRAPEAU SEUL,
 * SANS bornes (G0-S17-16) :
 *   • `masked: true`  ⇒ effectif sous le seuil d'anonymat : indicateur MASQUÉ, `orange` non porté
 *     (le front affiche un message neutre, rien d'exploitable — G0-S17-3) ;
 *   • `masked: false` ⇒ `orange` est le VERDICT de l'API : `true` = TJM ≥ P75 (au-dessus du
 *     marché) ⇒ le front affiche le libellé + icône orange ; `false` = pas-orange ⇒ rien.
 * AUCUNE borne P25/P75, AUCUN effectif n'est transmis : le front ne dérive RIEN.
 */
export interface PositioningIndicatorDto {
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  /** true ⇒ indicateur masqué (effectif < gate K-anon). `orange` non significatif. */
  readonly masked: boolean;
  /**
   * Verdict UNAIRE de l'API : true = TJM ≥ P75 (au-dessus du marché). false = pas-orange (rien à
   * afficher). Non significatif quand `masked === true`. JAMAIS dérivé côté front (G0-S17-16).
   */
  readonly orange: boolean;
}

/**
 * Réponse de l'indicateur F-10 — MIROIR EXACT du `PositioningResponseDto` de l'API (route GELÉE D1,
 * `GET /supplier/positioning/:campaignId`). La RESSOURCE est la CAMPAGNE (`campaignId`), pas la
 * submission. `cycleNumber` = le cycle interrogé (Cycle 2 pour F-10). `indicators` = les indicateurs
 * UNAIRES `{ masked, orange }` des couples VERDICTÉS par l'API pour le `tjmValueEur` transmis :
 *   • requête PER-COUPLE (profileCode + seniorityLevel + tjmValueEur) ⇒ 1 indicateur (ce couple) ;
 *   • requête sans tjm (sonde d'activation) ⇒ les couples du marché, tous `orange:false`
 *     (l'API ne verdicte « orange » que contre un tjm transmis).
 *
 * `cycle2Active` N'EXISTE PAS dans ce DTO (API GELÉE) : l'activation de l'indicateur est DÉRIVÉE
 * côté front par `indicators.length > 0` (l'API ne renvoie d'indicateurs que si des soumissions
 * Cycle 1 alimentent le marché). Le front ne porte AUCUNE règle métier (G0-S17-16).
 */
export interface PositioningResponseDto {
  readonly campaignId: string;
  readonly cycleNumber: number;
  readonly indicators: readonly PositioningIndicatorDto[];
}

/**
 * Paramètres de la lecture F-10 (query string de la route GELÉE). `cycleNumber` requis (Cycle 2
 * pour l'indicateur). Le TRIPLET `profileCode` + `seniorityLevel` + `tjmValueEur` cible UN couple
 * et le fait VERDICTER par l'API contre la valeur SAUVEGARDÉE de ce couple ; omis ⇒ sonde
 * d'activation (couples du marché sans verdict orange). `tjmValueEur` = la valeur COMMITTÉE par
 * l'autosave (PAS la frappe en cours) : ENF-01 préservé (la query ne re-fire que sur valeur
 * sauvegardée, jamais par keystroke ; TanStack dédoublonne par clé).
 */
export interface PositioningQuery {
  readonly cycleNumber: number;
  readonly profileCode?: string | undefined;
  readonly seniorityLevel?: RateSeniorityLevelDto | undefined;
  readonly tjmValueEur?: number | undefined;
}

/**
 * Erreur applicative renvoyée par l'API positionnement (HTTP 4xx/5xx). L'UI l'affiche telle quelle ;
 * elle ne réinterprète JAMAIS la règle métier (autorité = API, notamment le verdict orange et le
 * masquage d'anonymat).
 */
export class PositioningApiError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = 'PositioningApiError';
  }
}

/**
 * Contrat du client FOURNISSEUR de l'indicateur F-10 UNAIRE — miroir de l'endpoint GELÉ D1
 * (`GET /supplier/positioning/:campaignId`). Aucune règle métier ici : le verdict orange (tjm ≥ P75)
 * ET le masquage d'anonymat sont l'autorité de l'API. L'UI relaie l'intention (campaignId d'URL =
 * ressource ; siret/contact viennent de la session ; profileCode/seniorityLevel/tjmValueEur =
 * query). La requête est PER-COUPLE : le front transmet la valeur SAUVEGARDÉE du couple à verdicter.
 */
export interface PositioningClient {
  /**
   * GET l'indicateur UNAIRE (drapeau orange/masqué) du couple ciblé par `query` (profileCode +
   * seniorityLevel + tjmValueEur SAUVEGARDÉ), au sein de la campagne `campaignId` / `cycleNumber`.
   * Sans triplet de couple ⇒ sonde d'activation (couples du marché, sans verdict orange). Les
   * couples sous le seuil d'anonymat reviennent MASQUÉS (`masked: true`, sans verdict). Aucune
   * borne, aucun effectif (G0-S17-3 / G0-S17-16).
   */
  getPositioning(
    campaignId: string,
    query: PositioningQuery,
  ): Promise<PositioningResponseDto>;
}
